<template>
    <modal size="xl" @close="$emit('close')" ref="modal" :is-closeable="isCloseable" :autoshow="true">
        <template v-slot:title>
            <span v-if="plan">Update Accrual Plan: </span><span v-if="plan" class="text-thin text-nowrap">{{ plan.name }}</span>
            <span v-if="!plan">Add New Accrual Plan</span>
        </template>
        <slot>
            <form class="mui-form" @submit.prevent="onSubmit()">
                <div class="form-row">
                    <div class="col-6">
                        <form-input v-model="formData.name" :errors="errors.name" type="text" label="Plan Name" />
                    </div>
                    <div class="col-6">
                        <form-input
                            v-model="formData.display_name"
                            :errors="errors.display_name"
                            type="text"
                            label="Display Name"
                            helper-text="Shown on earnings statements."
                        />
                    </div>
                </div>

                <div class="form-row">
                    <div class="col-6">
                        <form-input v-model="formData.plan_type" :errors="errors.plan_type" type="select" label="Plan Type" :options="planTypeOptions" />
                    </div>
                    <div class="col-6">
                        <form-input v-model="formData.accrual_year" :errors="errors.accrual_year" type="select" label="Accrual Year Type" :options="accrualYearOptions" />
                    </div>
                </div>

                <div class="form-row" v-if="!client">
                    <div class="col-6">
                        <form-input v-model="formData.state" :errors="errors.state" type="select" label="State" :options="stateOptions" />
                    </div>
                </div>

                <h5 class="text-uppercase mt-3 mb-1">Accrual Method</h5>
                <div class="form-row">
                    <div class="col">
                        <form-input v-model="formData.method" :errors="errors.method" type="radio" label="" :options="methodOptions" />
                    </div>
                </div>

                <h5 class="text-uppercase mt-3 mb-1">Eligibility</h5>
                <div class="form-row">
                    <div class="col-6">
                        <form-input v-model="formData.eligible_employment" :errors="errors.eligible_employment" type="radio" label="Employment" :options="eligibleEmploymentOptions" />
                    </div>
                    <div class="col-6">
                        <form-input v-model="formData.eligible_compensation" :errors="errors.eligible_compensation" type="radio" label="Compensation" :options="eligibleCompensationOptions" />
                    </div>
                </div>
                <div class="form-row">
                    <div class="col-6">
                        <form-input
                            v-model="formData.eligible_hired_on_or_after"
                            :errors="errors.eligible_hired_on_or_after"
                            type="date"
                            label="Hire Date Application"
                            helper-text="Applies to employees hired on or after this date. Blank for all."
                        />
                    </div>
                </div>

                <h5 class="text-uppercase mt-3 mb-1">Termination and Rehire</h5>
                <div class="form-row">
                    <div class="col-6">
                        <form-input
                            v-model="formData.rehire_window_days"
                            :errors="errors.rehire_window_days"
                            type="integer"
                            postfix="days"
                            label="Rehire Window"
                            helper-text="Rehired within this window keeps hours and tenure date."
                        />
                    </div>
                </div>
                <div class="form-row mb-4">
                    <div class="col">
                        <form-input v-model="formData.payout_on_termination" :errors="errors.payout_on_termination" type="checkbox" label="Pay Out Balance on Termination" />
                    </div>
                </div>

                <div class="card mb-2">
                    <div class="card-header pr-1 d-flex align-items-center">
                        <span class="btnlike mr-auto">
                            <h4 class="mb-0">Tier 1 (Base)</h4>
                        </span>
                    </div>

                    <div class="card-body py-1">
                        <tier-settings-fields v-model:settings="formData.base_tier" :errors="errors" :method="formData.method">
                            <div class="form-row">
                                <div class="col-4">
                                    <form-input v-model="formData.accrual_waiting_value" :errors="errors.accrual_waiting_days || errors.accrual_waiting_hours" type="integer" label="Accrual Waiting Period" />
                                </div>
                                <div class="col-2">
                                    <form-input v-model="formData.accrual_waiting_unit" type="select" label="" :noblank="true" :options="waitingUnitOptions" />
                                </div>
                                <div class="col-6">
                                    <form-input v-model="formData.usage_waiting_days" :errors="errors.usage_waiting_days" type="integer" postfix="days" label="Usage Waiting Period" />
                                </div>
                            </div>
                            <div class="form-row">
                                <div class="col-6">
                                    <form-input
                                        v-model="formData.min_hours_per_period"
                                        :errors="errors.min_hours_per_period"
                                        type="decimal"
                                        label="Minimum Hours per Pay Period"
                                        helper-text="Nothing accrues in a period below this."
                                    />
                                </div>
                            </div>
                        </tier-settings-fields>
                    </div>
                </div>

                <div class="card mb-2" v-for="(tier, idx) in formData.tiers" :key="tier._key">
                    <div class="card-header pr-1 d-flex align-items-center">
                        <span class="btnlike mr-auto">
                            <h4 class="mb-0">Tier {{ idx + 2 }}</h4>
                        </span>
                        <button class="btn btn-outline-primary" type="button" @click.prevent="removeTier(idx)">Remove</button>
                    </div>

                    <div class="card-body py-1">
                        <form-errors :errors="tierErrors(idx).__all__"/>

                        <div class="form-row">
                            <div class="col-4">
                                <form-input v-model="tier.starts_at_value" :errors="tierErrors(idx).starts_after_months" type="integer" label="Starts At" />
                            </div>
                            <div class="col-2">
                                <form-input v-model="tier.starts_at_unit" type="select" label="" :noblank="true" :options="startsAtUnitOptions" />
                            </div>
                        </div>

                        <tier-settings-fields v-model:settings="formData.tiers[idx]" :errors="tierErrors(idx)" :method="formData.method" />
                    </div>
                </div>

                <button class="btn btn-outline-primary" type="button" @click.prevent="addTier()">
                    <i class="far fa-fw fa-plus"></i> Add Premium Tier
                </button>
            </form>
        </slot>

        <template v-slot:footer>
            <form-errors :errors="formErrors"/>

            <button-row>
                <button type="button" class="btn btn-block btn-outline-primary" @click.prevent="close()">Cancel</button>
                <button type="button" class="btn btn-block btn-primary" @click.prevent="onSubmit()">Save</button>
            </button-row>
        </template>
    </modal>
</template>

<script>
import FormMixin from '@/mixins/Form'
import ModalMixin from '@/mixins/ModalMixin'
import STATES from '@/states'
import TierSettingsFields from './TierSettingsFields'
import { TIER_SETTINGS_FIELDS } from './tiers'

let nextTierKey = 1

function makeSettings(source) {
    const result = {}
    TIER_SETTINGS_FIELDS.forEach(field => {
        result[field] = source ? source[field] : ''
    })
    if (!source) {
        result.rollover_enabled = false
    }
    return result
}

// With `client`, the plan is that client's own, edited through its ClientAccrualPlan
// (`clientPlanId`); without it, a global plan.
export default {
    props: ['plan', 'client', 'clientPlanId', ],
    mixins: [FormMixin, ModalMixin, ],
    components: {TierSettingsFields, },
    computed: {
        stateOptions() {
            return [{text: '-', value: ''}].concat(STATES.map(s => ({text: s.name, value: s.abbreviation})))
        },
        planTypeOptions() {
            return [
                {text: 'Vacation', value: 'vacation'},
                {text: 'Sick', value: 'sick'},
                {text: 'Other', value: 'other'},
            ]
        },
        methodOptions() {
            return [
                {text: 'Pay Cycle', value: 'pay-period'},
                {text: 'Hours Worked', value: 'hours-worked'},
                {text: 'Set Balance', value: 'set-balance'},
            ]
        },
        eligibleEmploymentOptions() {
            return [
                {text: 'Full Time', value: 'full-time'},
                {text: 'Part Time', value: 'part-time'},
                {text: 'Both Full and Part Time', value: 'both'},
            ]
        },
        eligibleCompensationOptions() {
            return [
                {text: 'Salary', value: 'salary'},
                {text: 'Hourly', value: 'hourly'},
                {text: 'Both Salary and Hourly', value: 'both'},
            ]
        },
        waitingUnitOptions() {
            return [
                {text: 'Days', value: 'days'},
                {text: 'Hours Worked', value: 'hours'},
            ]
        },
        startsAtUnitOptions() {
            return [
                {text: 'Years', value: 'years'},
                {text: 'Months', value: 'months'},
            ]
        },
        accrualYearOptions() {
            return [
                {text: 'Calendar Year', value: 'calendar'},
                {text: 'Anniversary Year', value: 'anniversary'},
            ]
        },
    },
    data() {
        return {
            formData: this.makeFormData(),
        }
    },
    methods: {
        makeTier(tier) {
            // Shown in years when the start is a whole number of years.
            const months = tier ? Number(tier.starts_after_months) : null
            const inYears = !tier || months % 12 == 0
            return {
                _key: nextTierKey++,
                id: tier ? tier.id : null,
                starts_at_value: tier ? (inYears ? months / 12 : months) : '',
                starts_at_unit: inYears ? 'years' : 'months',
                ...makeSettings(tier),
            }
        },
        makeFormData() {
            if (this.plan) {
                // The API stores waiting days and waiting hours separately; the form picks one.
                const waitsOnHours = Number(this.plan.accrual_waiting_hours) > 0
                return {
                    name: this.plan.name,
                    display_name: this.plan.display_name,
                    state: this.plan.state,
                    plan_type: this.plan.plan_type,
                    method: this.plan.method,
                    accrual_year: this.plan.accrual_year,
                    accrual_waiting_value: waitsOnHours ? Number(this.plan.accrual_waiting_hours) : this.plan.accrual_waiting_days,
                    accrual_waiting_unit: waitsOnHours ? 'hours' : 'days',
                    usage_waiting_days: this.plan.usage_waiting_days,
                    min_hours_per_period: this.plan.min_hours_per_period,
                    payout_on_termination: this.plan.payout_on_termination,
                    rehire_window_days: this.plan.rehire_window_days,
                    eligible_employment: this.plan.eligible_employment,
                    eligible_compensation: this.plan.eligible_compensation,
                    eligible_hired_on_or_after: this.plan.eligible_hired_on_or_after || '',
                    base_tier: makeSettings(this.plan),
                    tiers: [...this.plan.tiers]
                        .sort((a, b) => a.starts_after_months - b.starts_after_months)
                        .map(t => this.makeTier(t)),
                }
            } else {
                return {
                    name: '',
                    display_name: '',
                    state: '',
                    plan_type: 'sick',
                    method: 'hours-worked',
                    accrual_year: 'calendar',
                    accrual_waiting_value: 0,
                    accrual_waiting_unit: 'days',
                    usage_waiting_days: 0,
                    min_hours_per_period: 0,
                    payout_on_termination: false,
                    rehire_window_days: 0,
                    eligible_employment: 'both',
                    eligible_compensation: 'both',
                    eligible_hired_on_or_after: '',
                    base_tier: makeSettings(),
                    tiers: [],
                }
            }
        },
        addTier() {
            this.formData.tiers.push(this.makeTier())
        },
        removeTier(idx) {
            this.formData.tiers.splice(idx, 1)
        },
        tierErrors(idx) {
            return (this.errors.tiers || [])[idx] || {}
        },
        getFormData() {
            const data = JSON.parse(JSON.stringify(this.formData))
            // The API takes the base tier's settings as plan fields.
            Object.assign(data, data.base_tier)
            delete data.base_tier

            const waitingValue = data.accrual_waiting_value || 0
            data.accrual_waiting_days = data.accrual_waiting_unit == 'days' ? waitingValue : 0
            data.accrual_waiting_hours = data.accrual_waiting_unit == 'hours' ? waitingValue : 0
            delete data.accrual_waiting_value
            delete data.accrual_waiting_unit

            data.eligible_hired_on_or_after = data.eligible_hired_on_or_after || null
            if (this.client) {
                data.state = ''
            }

            data.tiers.forEach(tier => {
                const value = tier.starts_at_value === '' ? '' : Number(tier.starts_at_value)
                tier.starts_after_months = (value !== '' && tier.starts_at_unit == 'years') ? value * 12 : value
                delete tier.starts_at_value
                delete tier.starts_at_unit
                delete tier._key
                if (!tier.id) {
                    delete tier.id
                }
            })
            return data
        },
        onSubmit() {
            if (!this.validate()) {
                return
            }

            let promise
            if (this.client) {
                const base = `/clients/${this.client.id}/settings/accrual-plans`
                promise = this.plan
                    ? this.$api.put(`${base}/${this.clientPlanId}/plan`, this.getFormData())
                    : this.$api.post(`${base}/custom`, this.getFormData())
            }
            else if (this.plan) {
                promise = this.$api.put(`/admin/accruals/plans/${this.plan.id}`, this.getFormData())
            }
            else {
                promise = this.$api.post(`/admin/accruals/plans`, this.getFormData())
            }

            this.$store.dispatch('START_LOADING')
            return promise.then(resp => {
                this.$store.dispatch('STOP_LOADING')
                this.$emit('updated', resp)
                this.close()
            }).catch(errors => {
                this.$store.dispatch('STOP_LOADING')
                this.errors = errors
                this.formErrors = errors.__all__
                this.onError()
            })
        },
    }
}
</script>
